<template>
  <section class="order-view">
    <dcLoading v-if="loading"></dcLoading>
    <div class="order-view__grid" :class="gridClasses">
      <OrderHeader class="order-view__header" :order="order" :status="status" :amount-owed="amountOwed"
        :customer-linkable="isOfficeStaff" @view-customer="emitEvent('view-customer', $event)" />

      <!-- Opens in place, directly under the header on a phone, so taking a payment never means scrolling past the statement. -->
      <section v-if="isPayable" class="order-view__pay" aria-label="Take payment">
        <button v-if="!showPaymentForm" type="button" class="order-view__primary" data-testid="admin-order-take-payment"
          @click="openPaymentForm">Take payment</button>
        <div v-else class="order-view__pay-panel">
          <div class="order-view__pay-heading">
            <h2>Take payment</h2>
            <button type="button" class="order-view__text-button" @click="showPaymentForm = false">Cancel</button>
          </div>
          <form @submit.prevent="submitPayment(payment)" name="paymentForm" novalidate>
            <fieldset>
              <div>
                <div class="form-group" v-if="context !== 'student'"
                  :class="{ 'form-group--error': $v.payment.payment_method.$error }">
                  <label for="payment_method">Payment method</label>
                  <select id="payment_method" class="form-control" v-model="payment.payment_method" required
                    @blur="validateInput('payment_method')">
                    <option v-for="(option, index) in payment_methods" :value="option.value" :key="index">
                      {{ option.name }}
                    </option>
                  </select>
                </div>
                <div class="form-group" :class="{ 'form-group--error': $v.payment.payment_amount.$error }">
                  <label for="payment_amount">Amount</label>
                  <input id="payment_amount" class="form-control" type="text" placeholder="$0.00"
                    v-model.lazy="payment.payment_amount" v-money="money" required
                    @input="validateInput('payment_amount')" data-testid="admin-order-payment-amount" />
                </div>
                <p class="order-view__surcharge"
                  v-if="surchargeRequired && payment.payment_method === 'card' && paymentInCents > 0">
                  Card payments add a {{ surchargeAmount }}% fee. This card will be charged
                  <strong>{{ formatAmount(paymentTotal) }}</strong>.
                </p>
              </div>
            </fieldset>
            <section v-if="payment.payment_method === 'check'">
              <fieldset>
                <div class="form-group row">
                  <div class="col-xs-12">
                    <label for="check_number">Check number (optional)</label>
                    <input id="check_number" title="Check Number" class="form-control" v-model="payment.check_number" />
                  </div>
                </div>
              </fieldset>
            </section>

            <section v-if="payment.payment_method === 'card'">
              <div class="text-center" v-if="billingConfig.gateway === 'stripe' && !stripeElementOptions.amount"
                style="margin-bottom: 2em;">
                <button class="btn btn-dc btn-block" :disabled="!payment.payment_amount"
                  @click.prevent="proceedToPayment(payment.payment_amount)">New Payment Method</button>
              </div>

              <div class="text-center" v-if="tokenizationSupported" style="margin-bottom: 2em;">
                <button class="btn btn-dc btn-block" :disabled="!payment.payment_amount"
                  @click.prevent="toggleUseExistingPaymentMethod()">Use Existing Payment Method</button>
              </div>
            </section>

            <section v-if="payment.payment_method === 'card'">
              <section v-if="showUseExistingPaymentMethodUI && tokenizationSupported && order.student"
                style="margin-bottom: 2em;">
                <h3>Use Existing Payment Method</h3>

                <div class="well" v-if="paymentMethods.length === 0">
                  There are no payment methods for this student yet.
                </div>

                <div class="well" v-for="paymentMethod in paymentMethods" :key="paymentMethod.id">
                  <p><strong>Name</strong>: {{ paymentMethod.name }}</p>
                  <p><strong>Address</strong>: {{ paymentMethod.street }},
                    {{ paymentMethod.city }},
                    {{ paymentMethod.state }}
                    {{ paymentMethod.zip }}</p>

                  <p>Card Number: {{ paymentMethod.last4 }}</p>
                  <p>Card Expiration Date {{ paymentMethod.card_expiration_date }}</p>
                  <p>Card Type: {{ paymentMethod.card_type }}</p>
                  <div style="margin-top: 1em;">
                    <button class="btn btn-dc" :disabled="payment.card.payment_method_id === paymentMethod.id"
                      @click.prevent="selectPaymentMethod(paymentMethod.id)">
                      Select Payment Method
                    </button>
                  </div>
                </div>
              </section>

              <fieldset>
                <section
                  v-if="billingConfig.gateway === 'authorize' && payment.payment_method === 'card' && payment.card.use_existing_payment_method === false">
                  <AuthorizeCard :apiClientKey="billingConfig.authorize_api_client_key"
                    :apiLoginId="billingConfig.authorize_api_login_id" :months="months" :years="years" :states="states"
                    @cardTokenCreated="handleCompletedToken"
                    :includeBillingAddress="!billingConfig.gateway_config.client_id"
                    :billingAddress="payment.card.billing_address" :buttonText="submitLabel"
                    :tokenizationSupported="!!billingConfig.gateway_config.client_id" :buttonDisabled="false">
                  </AuthorizeCard>
                  <!-- DS-PORT-DEFECT (drivescout-vue3 M4 slice 3, 2026-09-28): row F-dbl: every card component here (AuthorizeCard, StripeCard, SquareCard, PayPalCard) gets buttonDisabled false and none keeps its own busy state, so its Submit stays enabled while a payment is in flight and a double click sends the payment twice; since 2026-10-10 both sends carry the host's held request_id and body, so the second replays or answers 409 payment_in_progress instead of charging again, but it still flashes that 409; fixed in the port. -->
                </section>
                <section
                  v-if="billingConfig.gateway === 'stripe' && stripeElementOptions.amount > 0 && payment.payment_method === 'card' && payment.card.use_existing_payment_method === false">
                  <StripeCard :publishableKey="billingConfig.gateway_config.stripe_publishable_key"
                    :elementOptions="stripeElementOptions" :buttonText="submitLabel"
                    @stripeCardTokenCreated="handleCompletedToken" :buttonDisabled="false" :returnUrl="returnUrl">
                  </StripeCard>
                </section>
                <section
                  v-if="billingConfig.gateway === 'square' && payment.payment_method === 'card' && payment.card.use_existing_payment_method === false">
                  <SquareCard :applicationId="billingConfig.gateway_config.square_application_id"
                    :accessToken="billingConfig.gateway_config.square_access_token"
                    :environment="billingConfig.gateway_config.square_environment" :buttonText="submitLabel"
                    @squareCardTokenCreated="handleCompletedToken" :button-disabled="false">
                  </SquareCard>
                </section>

                <section
                  v-if="billingConfig.gateway === 'paypal' && payment.payment_method === 'card' && payment.card.use_existing_payment_method === false">
                  <PayPalCard :clientId="billingConfig.gateway_config.paypal_rest_api_client_id"
                    :beforeSubmit="setStudentCart" :buttonText="submitLabel" :buttonDisabled="false"
                    :createOrder="createPaypalOrder" :includeBillingAddress="true" :skipCaptureOnApprove="true"
                    @paypalCardTokenCreated="handlePaypalCardTokenCreated">
                  </PayPalCard>
                </section>
              </fieldset>

            </section>
            <fieldset>
              <div class="form-group"
                v-if="billingConfig.gateway === 'none' || (payment.card.use_existing_payment_method === true && payment.card.payment_method_id) || (payment.payment_method !== '' && payment.payment_method !== 'card')">
                <button class="order-view__primary"
                  :disabled="(paymentFormInvalid && payment.card.use_existing_payment_method === false) || submitted === true"
                  type="submit">{{ submitLabel }} <i class="fa fa-circle-o-notch fa-spin fa-fw"
                    v-if="submitted"></i></button>
              </div>
            </fieldset>
          </form>
        </div>
      </section>

      <OrderStatement class="order-view__statement" :lines="statementLines" :payments-linkable="isOfficeStaff"
        @view-payment="emitEvent('view-payment', $event)" />

      <section v-if="hasNotes" class="order-view__notes" aria-label="Notes">
        <h2>Notes</h2>
        <p v-for="note in order.notes" :key="note.id" class="order-view__note">{{ note.content }}</p>
      </section>

      <OrderAttribution v-if="showsAttribution" class="order-view__attribution" :order="order" />

      <section class="order-view__tools" aria-label="Receipt and order tools">
        <template v-if="context !== 'student'">
          <label for="receipt_email">Send receipt to</label>
          <div class="order-view__receipt">
            <input type="email" class="form-control" id="receipt_email" autocomplete="email" v-model="receipt_email" />
            <button type="button" class="order-view__secondary" :disabled="submitted"
              @click.prevent="emitEvent('send-receipt', { order_id: order.id, receipt_email: receipt_email })">Send</button>
          </div>
        </template>
        <!-- The API answers with a PDF's URL and the host saves it (saveToDisk), so this downloads rather than prints. -->
        <button type="button" class="order-view__text-button order-view__download" :disabled="submitted"
          @click.prevent="emitEvent('print-order', { order_id: order.id })">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M12 4v11" />
            <path d="M7 10l5 5 5-5" />
            <path d="M5 20h14" />
          </svg>
          Download PDF
        </button>
        <div v-if="context !== 'student'" class="order-view__void">
          <button type="button" class="order-view__text-button order-view__text-button--danger"
            data-testid="admin-order-void" :disabled="voidDisabled"
            @click="emitEvent('void-order', { order_id: order.id })">Void order</button>
          <p v-if="voidReason" class="order-view__help">{{ voidReason }}</p>
          <p v-if="billingConfig.gateway === 'authorize'" class="order-view__help">Authorize.Net void eligibility
            depends on
            settlement, not a fixed number of hours. Verify the transaction in the Merchant Center when needed.</p>
        </div>
      </section>
    </div>
  </section>
</template>

<script>
import { VMoney } from 'v-money';
//form validation
import { validationMixin } from 'vuelidate';
import { required, requiredIf, minLength, numeric } from 'vuelidate/lib/validators';

import AuthorizeCard from '../authorize-card/AuthorizeCard.vue';
import StripeCard from '../stripe-card/StripeCard.vue';
import SquareCard from '../square-card/SquareCard.vue';
import PaypalCard from '../paypal-card/PayPalCard.vue';
import dcLoading from '../vue-directives/loading/Loading.vue';
import OrderHeader from './OrderHeader.vue';
import OrderStatement from './OrderStatement.vue';
import OrderAttribution from './OrderAttribution.vue';
import { ORDER_STATUS, isVoidedLineItem, orderStatus, statementLines, voidBlockedReason } from './orderView';

import { inDollars, percentageOfXInCents } from "../../../../DriveScout/Billing/Teller";
import { PaymentMethods } from "../../constants/payment_methods";
import { States } from "../../constants/states";
import { Months, Years } from "../../constants/months_and_years";
import Dinero from 'dinero.js';
import { PaymentOutcome, PaymentRequest } from '../../payments/PaymentRequest';

const paymentInitData = {
  payment_amount: '$0.00',
  payment_method: '',
  card: {
    name_on_card: '',
    payment_method_id: null,
    use_existing_payment_method: false,
    billing_address: {
      street: '',
      city: '',
      state: '',
      zip: ''
    },
    number: '',
    expiration: '', cvc: ''
  }
}; //used to reset form
const mustBeGreaterThanZero = (amount) => parseInt(amount.replace(/\D/g, '')) > 0 //used to validate payment amount

// billingConfig is OrderDetailsBillingConfig
// order is IOrder & { payments?: IPayment[]; notes?: INote[]; student?: IUser; }

export default {
  name: 'dcOrder',
  components: { dcLoading, OrderHeader, OrderStatement, OrderAttribution, AuthorizeCard, StripeCard, SquareCard, PaypalCard, PayPalCard: PaypalCard },
  directives: { money: VMoney },
  mixins: [validationMixin],
  props: {
    'billingConfig': { type: Object, default: () => { } },
    'order': { type: Object, required: true },
    'loading': { type: Boolean, default: false },
    'context': { type: String, required: true },
    'clearPaymentForm': { type: Boolean, default: false }, //change this value to true from angular after form submit to clear form
    'submitted': { type: Boolean, default: false },
  },
  data() {
    return {
      cardErrors: {
        number: false,
        expiration_month: false,
        expiration_year: false,
        cvc: false,
        receipt_email: '',
      },
      money: {
        decimal: '.',
        thousands: ',',
        prefix: '$',
        precision: 2,
        masked: false /* doesn't work with directive */
      },
      months: Months,
      paymentMethods: [],
      payment: {
        payment_amount: '$0.00',
        save_payment_method: false,
        payment_method: '',
        card: {
          name_on_card: '',
          billing_address: {
            street: '',
            city: '',
            state: '',
            zip: ''
          },
          number: '',
          expiration_month: '',
          expiration_year: '',
          cvc: '',
          payment_method_id: null,
          use_existing_payment_method: false,
        }
      },
      stripeElementOptions: {
        amount: undefined,
        currency: 'usd',
      },
      showUseExistingPaymentMethodUI: false,
      payment_methods: PaymentMethods, //array
      paymentInitData: paymentInitData,
      states: States,
      years: Years,
      receipt_email: this.order.student ? this.order.student.email || '' : '',
      showPaymentForm: false,
    }
  },
  validations: {
    payment: {
      payment_amount: { required, mustBeGreaterThanZero },
      payment_method: { required },
      card: {
        name_on_card: { required: requiredIf(function () { return this.isCardPayment }), minLength: minLength(3) },
        billing_address: {
          street: { required: requiredIf(function () { return this.isCardPayment }), minLength: minLength(3) },
          city: { required: requiredIf(function () { return this.isCardPayment }), minLength: minLength(2) },
          state: { required: requiredIf(function () { return this.isCardPayment }), minLength: minLength(2) },
          zip: { required: requiredIf(function () { return this.isCardPayment }), minLength: minLength(5), numeric }
        },
        number: { required: requiredIf(function () { return this.isCardPayment }) },
        expiration_month: { required: requiredIf(function () { return this.isCardPayment }) },
        expiration_year: { required: requiredIf(function () { return this.isCardPayment }) },
        cvc: { required: requiredIf(function () { return this.isCardPayment }) }
      }
    }
  },
  methods: {
    handleAuthorizeCard(tokenData) {
      // console.log(tokenData);
    },
    handleCompletedConfirmationToken(tokenData) {
      // console.log(tokenData);
    },
    /**
     * Handle successful PayPal card payment
     * Captures the payment and emits success to parent
     */
    async handlePaypalCardTokenCreated(tokenData) {
      try {
        // Capture the payment on the backend
        const result = await this.$store.dispatch('payments/CAPTURE_PAYPAL_PAYMENT_ORDER', {
          order_id: this.order.id,
          paypal_order_id: tokenData.paypal_order_id,
        });
        this.paypalApproval.settled(PaymentOutcome.answered(result));

        // Emit success to parent to reload order data
        this.emitEvent('payment-success', {
          message: result.message,
          payment: result.payment,
          order: result.order,
        });
      } catch (error) {
        this.paypalApproval.settled(PaymentOutcome.failed(error));
        console.error('PayPal payment capture failed:', error);
        this.emitEvent('payment-error', {
          message: error.message || 'Payment failed',
        });
      }
    },
    /**
     * Create a PayPal order for making a payment on this existing order
     */
    async createPaypalOrder() {
      const paymentAmount = this.paymentInCents;
      if (!paymentAmount || paymentAmount <= 0) {
        throw new Error('Payment amount is required');
      }

      const approval = { order_id: this.order.id, payment_amount: paymentAmount };
      let result;
      try {
        result = await this.$store.dispatch(
          'payments/CREATE_PAYPAL_PAYMENT_ORDER',
          this.paypalApproval.bodyFor(approval, (requestId) => ({ ...approval, request_id: requestId }))
        );
      } catch (error) {
        this.paypalApproval.settled(PaymentOutcome.failed(error));
        throw error;
      }

      return {
        paypal_order_id: result.paypal_order_id,
        drivescout_order_id: result.order_id,
      };
    },
    /**
     * Placeholder for setStudentCart - not needed for payments on existing orders
     */
    setStudentCart() {
      // Not needed for payments on existing orders, but required by PayPalCard component
      return Promise.resolve();
    },

    emitEvent: function (eventName, dataObject) {
      this.$emit(eventName, dataObject)
    },
    formatAmount: function (amount) {
      return inDollars(amount)
    },
    openPaymentForm() {
      this.payment.payment_amount = this.formatAmount(this.amountOwed);
      this.showPaymentForm = true;
    },
    handleClose() {
      this.receipt_email = '';
      this.emitEvent('close-modal');
    },
    handlePayPalPayment(details) {
      this.payment.charge_id = details.id;
      this.submitPayment();
    },
    resetPayment() {
      this.payment = JSON.parse(JSON.stringify(this.paymentInitData)); // DS-PORT-DEFECT (drivescout-vue3 M4 slice 3, 2026-09-28): row F-clear-form: resetPayment leaves stripeElementOptions.amount and showUseExistingPaymentMethodUI as they were, and the clearPaymentForm watch fires only on a change to true, which the instructor host makes once (order.controller.ts:190); fixed in the port.
      this.payment.payment_amount = this.formatAmount(this.amountOwed);
    },
    submitPayment(payment) {
      if (this.paymentFormInvalid && this.payment.card.use_existing_payment_method === false) {
        return false;
      }
      let p = structuredClone(payment);
      p.payment_amount = this.paymentInCents;
      p.order_id = this.order.id;
      if (this.context === 'student') {
        p.payment_method = 'card';
      }
      this.emitEvent('submit', { payment: p });
    },
    validateInput(key, nestedKey, nested2) {
      //im sure there's a better way to do this
      if (nested2) {
        return this.$v.payment[key][nestedKey][nested2].$touch()
      }
      else if (nestedKey) {
        return this.$v.payment[key][nestedKey].$touch()
      } else {
        return this.$v.payment[key].$touch()
      }
    },
    validateCCInput(key) {
      if (key === 'number') {
        //this.cardErrors.number = !this.$cardFormat.validateCardNumber(this.payment.card.number) //validate function returns true if valid;
      }
      else if (key === 'cvc') {
        //this.cardErrors.cvc = !this.$cardFormat.validateCardCVC(this.payment.card.cvc);
      }
    },
    applyProvidedCouponTo(amount) {
      // apply coupon if provided, otherwise proceed
      if (this.order.coupons.length > 0) {
        if (this.order.coupons[0].type() === 'fixed') {
          return amount - this.order.coupons[0].amount();
        }

        if (this.order.coupons[0].type() === 'percentage') {
          const subAmount = Dinero({ amount: amount, currency: 'USD' }).percentage(this.order.coupons[0].amount());
          const dinero = Dinero({ amount: amount, currency: 'USD' }).subtract(subAmount)
          return dinero.getAmount();
        }
      }

      return amount;
    },
    proceedToPayment(amount) {
      this.payment.card.payment_method_id = null;
      this.payment.card.use_existing_payment_method = false;
      this.showUseExistingPaymentMethodUI = false;

      this.stripeElementOptions.amount = this.paymentInCents;
    },
    toggleUseExistingPaymentMethod() {
      this.showUseExistingPaymentMethodUI = !this.showUseExistingPaymentMethodUI;

      if (this.showUseExistingPaymentMethodUI === true) {
        this.payment.card.payment_method_id = null;
        this.payment.card.use_existing_payment_method = true;
        delete this.stripeElementOptions.amount;
        this.$store.dispatch('payments/GET_STUDENT_PAYMENT_METHODS', this.order.student.id).then(paymentMethods => {
          this.paymentMethods = paymentMethods;
        });
      } else {
        this.payment.card.use_existing_payment_method = false;
        // delete this.payment.card.payment_method_id;
        delete this.stripeElementOptions.amount;
      }
    },
    handleCompletedToken(token) {
      // token is either a string, an authorize token, or a card object
      let payment = structuredClone(this.payment);
      payment.payment_amount = this.paymentInCents;

      if (payment.payment_method === 'card' && (this.billingConfig.gateway === 'stripe' || this.billingConfig.gateway === 'square')) {
        payment.card.token = token.token;
        payment.save_payment_method = token.save_payment_method;
      }

      if (payment.payment_method === 'card' && this.billingConfig.gateway === 'authorize' && this.tokenizationSupported) { // DS-PORT-DEFECT (drivescout-vue3 M4 slice 3, 2026-09-28): row a: AuthorizeCard is given billingConfig.authorize_api_client_key and authorize_api_login_id (its :apiClientKey and :apiLoginId above), which configureBilling never sets (they sit under gateway_config), so the card always emits the raw card, and with a client key this branch posts it as card.token without the opaqueData wrapper, which the API rejects with a 422; fixed in the port.
        payment.card.token = token;
      } else {
        payment.card = token;
      }

      this.emitEvent('submit', { payment: payment });
    },
    selectPaymentMethod(paymentMethodId) {
      this.payment.card.payment_method_id = paymentMethodId;
      this.payment.card.use_existing_payment_method = true;
    }
  },
  created() {
    // This component dispatches the PayPal approval and sees its capture, so
    // it holds the approval's key; the card payment's key is the host's.
    // Not in data(): the key needs no reactivity.
    this.paypalApproval = new PaymentRequest();
  },
  mounted() { },
  computed: {
    tokenizationSupported() {
      if (this.billingConfig.gateway === 'stripe') {
        return true;
      }

      if (this.billingConfig.gateway === 'authorize' && this.billingConfig.gateway_config.authorize_api_client_key) {
        return true;
      }

      if (this.billingConfig.gateway === 'square') {
        return true;
      }

      if (this.billingConfig.gateway === 'paypal') {
        return true;
      }

      return false;
    },
    amountOwed: function () {
      if (this.order.financial_summary) {
        return this.order.financial_summary.collection_closed ? 0 : this.order.financial_summary.collection_balance;
      }
      if (this.order.status === 'VOID' || this.order.status === 'REFUNDED') {
        return 0;
      } else {
        let amountPaid = 0;
        if (this.order.payments.length > 0) {
          amountPaid = this.order.payments.filter(payment => {
            return payment.void !== true; // DS-PORT-DEFECT (drivescout-vue3 M4 slice 3, 2026-09-28): row F-owed: on a read without financial_summary (the API sends it on every /details read, OrderDetailsController.php:41, and the financial_summary branch above then takes its collection_balance), Amount Owed subtracts every non-void payment, so a failed or pending payment counts as paid (the API counts succeeded ones only), and it starts from the coupon-applied line-item sum instead of order.total; fixed in the port (Amount Owed comes from the API's financial_summary).
          }).reduce((carry, payment) => {
            return carry + parseInt(payment.amount, 10);
          }, 0);
        }
        return this.applyProvidedCouponTo(parseInt(this.orderTotal)) - amountPaid;
      }
    },
    isCardPayment() {
      return this.payment.payment_method === 'card';
    },
    paymentFormInvalid: function () {
      return this.$v.payment.$invalid || (this.payment.payment_method === 'card' && Object.values(this.cardErrors).some(val => val === true));
    },
    paymentInCents: function () {
      return parseInt(this.payment.payment_amount.replace(/\D/g, ''));
    },
    surchargeAmount: function () {
      if (this.billingConfig.policyMeta.hasOwnProperty('surcharge_amount')) {
        return parseFloat(this.billingConfig.policyMeta.surcharge_amount);
      } else return 0;
    },
    surchargeAmountBilled: function () {
      if (this.surchargeBilledSeparately && this.payment.payment_method === 'card') {
        return inDollars(percentageOfXInCents(this.paymentInCents, this.surchargeAmount));
      }
    },
    surchargeBilledSeparately: function () {
      if (this.billingConfig.policyMeta.hasOwnProperty('surcharge_amount')) {
        return parseFloat(this.billingConfig.policyMeta.surcharge_amount) > 0; // DS-PORT-DEFECT (drivescout-vue3 M4 slice 3, 2026-09-28): row F-surcharge-gate: the surcharge notice tests only surcharge_amount above 0, not surcharge_on === 'surcharge_on' as the API does (School.php:757-765 at d555babbf), so it can promise a surcharge the API does not charge; fixed in the port.
      } else return false;
    },
    surchargeRequired: function () {
      return this.surchargeBilledSeparately && this.payment.payment_method === 'card';
    },
    // The line items that count: a voided card payment's Processing Fee line is
    // left out (see isVoidedLineItem), and so out of Amount Owed when the read has
    // no financial_summary. Shown as the statement's Subtotal when a coupon applies.
    orderTotal: function () {
      let orderTotalInCents = this.order.lineitems.filter(lineitem => !isVoidedLineItem(lineitem, this.order.payments)).reduce((price, product) => {
        return price + parseInt(product.price);
      }, 0);
      return orderTotalInCents;
    },
    status() {
      return orderStatus(this.order, this.amountOwed);
    },
    statementLines() {
      return statementLines(this.order, this.orderTotal, this.amountOwed);
    },
    // Office roles can open a payment and a student's profile; the instructor host binds neither.
    isOfficeStaff() {
      return this.context !== 'student' && this.context !== 'instructor';
    },
    isPayable() {
      return this.canTakePayment && this.order.paid === false && this.order.status !== ORDER_STATUS.VOID && this.order.status !== ORDER_STATUS.REFUNDED;
    },
    hasNotes() {
      return Array.isArray(this.order.notes) && this.order.notes.length > 0;
    },
    // Where the customer came from is the school's marketing data, for the office only.
    showsAttribution() {
      return this.isOfficeStaff && !!this.order.attribution_captured_at;
    },
    voidReason() {
      return voidBlockedReason(this.order);
    },
    voidDisabled() {
      return this.submitted || this.voidReason !== null;
    },
    // Names what the button does: a card is charged (surcharge included); cash, check or other is recorded.
    submitLabel() {
      const amount = this.formatAmount(this.paymentTotal || 0);
      return this.payment.payment_method === 'card' ? `Charge ${amount}` : `Record ${amount} payment`;
    },
    gridClasses() {
      return {
        'order-view__grid--payable': this.isPayable,
        'order-view__grid--notes': this.hasNotes,
      };
    },
    paymentTotal: function () {
      if (this.surchargeBilledSeparately && this.payment.payment_method === 'card') {
        return this.paymentInCents + percentageOfXInCents(this.paymentInCents, this.surchargeAmount);
      } else {
        return this.paymentInCents;
      }
    },
    canTakePayment() {
      if (this.order.financial_summary &&
        (this.order.financial_summary.collection_closed || this.order.financial_summary.reconciliation_required || this.order.financial_summary.payment_capacity <= 0)) {
        return false;
      }
      return this.context === 'administrator' || this.context === 'owner' || this.context === 'staff' || this.context === 'student' || (this.context === 'instructor' && this.billingConfig.policyMeta.instructor_can_take_payment === 'Y');
    },
    returnUrl() {
      return window.location.href;
    }
  },
  watch: {
    // whenever clearPaymentForm prop changes, this function will run
    clearPaymentForm: function (newValue, oldValue) {
      if (newValue === true) {
        this.resetPayment()
      }
    },
    // The hosts swap in a re-read order after mount; fill the receipt address unless someone typed one.
    'order.student': function (student) {
      if (this.receipt_email === '' && student) {
        this.receipt_email = student.email || '';
      }
    }
  },
}
</script>

<style lang="scss" scoped>
@import "../../../../assets/css/partials/variables.scss";

// One DOM order, two arrangements. On a phone the panels stack in reading
// order: header, payment, statement, notes, tools. From 992px the statement
// takes the left column and payment and tools share the right one.
.order-view__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas: "header" "statement" "tools";
  gap: 20px;
  align-items: start;
  padding: 8px 0 24px;
  color: $grey;
  font-size: 14px;
  line-height: 1.45;

  &.order-view__grid--payable {
    grid-template-areas: "header" "pay" "statement" "tools";
  }

  &.order-view__grid--notes {
    grid-template-areas: "header" "statement" "notes" "tools";
  }

  &.order-view__grid--payable.order-view__grid--notes {
    grid-template-areas: "header" "pay" "statement" "notes" "tools";
  }

  @media #{$nineninetwo} {
    grid-template-columns: minmax(0, 1fr) 340px;
    grid-template-areas: "header header" "statement tools";
    gap: 28px 40px;

    &.order-view__grid--payable {
      grid-template-areas: "header header" "statement pay" "statement tools";
    }

    &.order-view__grid--notes {
      grid-template-areas: "header header" "statement tools" "notes tools";
    }

    &.order-view__grid--payable.order-view__grid--notes {
      grid-template-areas: "header header" "statement pay" "statement tools" "notes tools";
    }
  }

  h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }
}

.order-view__header {
  grid-area: header;
}

.order-view__pay {
  grid-area: pay;
}

.order-view__statement {
  grid-area: statement;
}

.order-view__notes {
  grid-area: notes;

  h2 {
    margin-bottom: 8px;
    font-size: 14px;
  }
}

// Below the left column's panels from 992px, and last on a phone. No named area, which
// would double each of the grid's layouts for a panel only some orders have.
.order-view__attribution {
  grid-column: 1;
}

.order-view__tools {
  grid-area: tools;

  label {
    display: block;
    margin-bottom: 6px;
    font-size: 13px;
    font-weight: 600;
  }
}

.order-view__note {
  margin: 0 0 8px;
  padding: 12px 14px;
  border: 1px solid $light;
  border-radius: 4px;
  background: $white;
  white-space: pre-line;
}

.order-view__pay-panel {
  padding: 4px 16px 16px;
  border: 1px solid $light;
  border-radius: 4px;
  background: $white;
}

.order-view__pay-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.order-view__surcharge {
  margin: 0 0 15px;
  padding: 10px 12px;
  border-radius: 4px;
  background: mix($yellow, $white, 25%);
  font-size: 13px;
}

.order-view__receipt {
  display: flex;
  gap: 8px;

  .form-control {
    flex: 1;
    min-width: 0;
    height: 44px;
  }
}

.order-view__download {
  margin-top: 8px;
}

.order-view__void {
  margin-top: 8px;
  padding-top: 4px;
  border-top: 1px solid $light;
}

.order-view__help {
  margin: 0;
  font-size: 13px;
  color: $blue-grey;
}

// Buttons: one filled action per panel, an outlined secondary, and text actions.

.order-view__primary,
.order-view__secondary,
.order-view__text-button {
  font-family: inherit;
  font-weight: 600;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid $green;
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
  }
}

.order-view__primary {
  width: 100%;
  min-height: 48px;
  border: 0;
  border-radius: 4px;
  background: $green-ink;
  color: $white;
  font-size: 16px;

  &:hover:not(:disabled) {
    background: darken($green-ink, 6%);
  }

  &:disabled {
    background: $light-grey;
  }
}

.order-view__secondary {
  flex: none;
  height: 44px;
  padding: 0 18px;
  border: 1px solid $grey;
  border-radius: 4px;
  background: $white;
  color: $grey;
  font-size: 15px;

  &:disabled {
    border-color: $light-grey;
    color: $light-grey;
  }
}

.order-view__text-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0;
  border: 0;
  background: none;
  color: $green-ink;
  font-size: 15px;

  &:hover:not(:disabled) {
    text-decoration: underline;
  }

  &:disabled {
    color: $light-grey;
  }
}

.order-view__text-button--danger {
  color: $danger-ink;
}

.form-group--error {
  label {
    color: red;
  }

  input,
  select,
  .ql-container.ql-snow {
    border: 1px solid red !important;
  }
}
</style>
